import { cn } from "@/common/helpers/utils";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { inputVariants } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { ChevronDown, TrashIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BaseFilterProps, Filter, FilterOption, FilterPaginationState } from "./_types";

export interface BaseFilterComponentProps extends BaseFilterProps {
  labelSingular: string;
  labelPlural: string;
  options: FilterOption[];
  isLoading?: boolean;
  error?: Error | null;
  pagination?: FilterPaginationState;
}

export interface FilterComponentProps<TBody, TParams> extends BaseFilterProps {
  filter: Filter<TBody, TParams>;
  pagination?: FilterPaginationState;
}

export function BaseFilterComponent({
  labelSingular,
  labelPlural,
  options,
  selectedValues,
  onSelectionChange,
  placeholder = "common:Select",
  searchPlaceholder = "common:Search",
  emptyText = "common:NoOptionsFound",
  className,
  showBadges = true,
  showRemoveAllButton = true,
  disabled = false,
  isLoading = false,
  error,
  pagination,
}: BaseFilterComponentProps) {
  const { t } = useTranslation("common");

  const displayPlaceholder = placeholder?.includes(":") ? t(placeholder) : placeholder;
  const displaySearchPlaceholder = searchPlaceholder?.includes(":") ? t(searchPlaceholder) : searchPlaceholder;
  const displayEmptyText = emptyText?.includes(":") ? t(emptyText) : emptyText;
  const displayLabelSingular = labelSingular?.includes(":") ? t(labelSingular) : labelSingular;
  const displayLabelPlural = labelPlural?.includes(":") ? t(labelPlural) : labelPlural;

  const isServerSideSearch = !!pagination?.onSearchChange;

  const handleClear = () => {
    onSelectionChange([]);
  };

  const selectedOptions = options.filter((option) => selectedValues.includes(option.id));

  const comboboxOptions: ComboboxOption[] = options.map((option) => ({
    value: option.id,
    label: option.name?.includes(":") ? t(option.name) : option.name,
    searchText: option.name?.includes(":") ? t(option.name) : option.name,
  }));

  const handleComboboxChange = (value: string | string[]) => {
    if (Array.isArray(value)) {
      onSelectionChange(value);
    }
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <div className="flex w-full flex-row items-center gap-2">
        <Combobox
          multiple
          options={comboboxOptions}
          value={selectedValues}
          onChange={handleComboboxChange}
          isLoading={isLoading}
          shouldFilter={!isServerSideSearch}
          placeholder={displayPlaceholder}
          searchPlaceholder={displaySearchPlaceholder}
          emptyText={error ? t("ErrorLoadingOptions") : displayEmptyText}
          disabled={disabled}
          hasMore={pagination?.hasMore}
          remainingCount={pagination?.remainingCount}
          isLoadingMore={pagination?.isLoadingMore}
          onLoadMore={pagination?.onLoadMore}
          onSearchChange={pagination?.onSearchChange}
          renderTrigger={({ open, placeholder: triggerPlaceholder }) => (
            <Button
              variant={ButtonVariant.Input}
              role="combobox"
              aria-expanded={open}
              disabled={disabled || isLoading}
              className={cn(inputVariants(), "h-9 min-w-50 justify-between px-3 font-normal")}
              aria-label={open ? t("common:Collapse") : t("common:Expand")}
            >
              {isLoading ? (
                <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
              ) : (
                <span className={cn("truncate", selectedValues.length === 0 && "text-muted-foreground")}>
                  {selectedValues.length === 0
                    ? triggerPlaceholder
                    : selectedValues.length === 1
                      ? `${selectedValues.length} ${displayLabelSingular} ${t("Selected")}`
                      : `${selectedValues.length} ${displayLabelPlural} ${t("Selected")}`}
                </span>
              )}
              <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          )}
        />
        <Button
          variant={ButtonVariant.Outlined}
          colorVariant={ButtonColorVariant.Secondary}
          size={ButtonSize.Icon}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClear();
          }}
          className="shrink-0"
          disabled={selectedValues.length === 0 || disabled || isLoading}
          aria-label={t("Remove")}
        >
          <TrashIcon className="size-4" />
        </Button>
      </div>

      {showBadges && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map((option) => (
            <Badge
              key={option.id}
              variant={BadgeVariant.Filled}
              colorVariant={BadgeColorVariant.Secondary}
              className="cursor-pointer"
            >
              {option.name?.includes(":") ? t(option.name) : option.name}
              <button
                type="button"
                className="ml-1 rounded-full outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const newValues = selectedValues.filter((id) => id !== option.id);
                    onSelectionChange(newValues);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => {
                  const newValues = selectedValues.filter((id) => id !== option.id);
                  onSelectionChange(newValues);
                }}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          {selectedValues.length > 1 && showRemoveAllButton && (
            <Button variant={ButtonVariant.Ghost} size={ButtonSize.Sm} onClick={handleClear} className="h-6 px-2">
              {t("RemoveAll")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export function FilterComponent<TBody, TParams>({
  filter,
  pagination,
  ...props
}: FilterComponentProps<TBody, TParams>) {
  const { options, isLoading, error } = filter.getData();

  return (
    <BaseFilterComponent
      {...props}
      labelSingular={filter.effectiveLabelSingular}
      labelPlural={filter.effectiveLabelPlural}
      options={options}
      isLoading={isLoading}
      error={error}
      placeholder={filter.effectivePlaceholder}
      searchPlaceholder={filter.effectiveSearchPlaceholder}
      emptyText={filter.effectiveEmptyText}
      pagination={pagination}
    />
  );
}
